import { getConfig } from '@superbet-group/web.lib.config';
import {
    IsoLanguageCode,
    Locale,
} from '@superbet-group/web.types';
import { loadScript } from '@superbet-group/web.lib.utilities';
import { Link } from '@app/router/constants/Link';
import store from '@app/store';
import { ZendeskUserData } from './types';
import ZendeskService from '@core/services/zendesk/zendeskService';
import { Analytics as AnalyticsSDK } from '@superbet-group/web.analytics.sdk';
import { getAccountConfig } from '@accountFederation/config/accountConfig';

const languageMap: Record<Locale, string> = {
    [Locale.Romanian]: IsoLanguageCode.Romania,
    [Locale.Polish]: IsoLanguageCode.Poland,
    [Locale.English]: IsoLanguageCode.England,
    [Locale.Serbian]: 'sr',
    [Locale.EnglishBelgian]: IsoLanguageCode.England,
    [Locale.DutchBelgian]: IsoLanguageCode.Dutch,
    [Locale.FrenchBelgian]: IsoLanguageCode.French,
    [Locale.PortugueseBrazilian]: IsoLanguageCode.Portuguese,
    [Locale.EnglishBrazilian]: IsoLanguageCode.England,
    [Locale.Bulgarian]: IsoLanguageCode.Bulgarian,
};

const iframeTitle: Record<Locale, string> = {
    [Locale.Romanian]: 'Fereastra pentru mesagerie',
    [Locale.Polish]: '',
    [Locale.English]: 'Messaging window',
    [Locale.Serbian]: '',
    [Locale.EnglishBelgian]: 'Messaging window',
    [Locale.DutchBelgian]: 'Berichtenvenster',
    [Locale.FrenchBelgian]: 'Fenêtre de messagerie',
    [Locale.PortugueseBrazilian]: 'Janela de mensagens',
    [Locale.EnglishBrazilian]: 'Messaging window',
    [Locale.Bulgarian]: '',

};

type DepartmentEntry = {
    id: number;
    name: string;
    status: 'online' | 'offline' | 'away';
};

export type ZendeskMetaDataFlags = {
    IS_VIP?: string;
};
enum Department {
    ChatBot = 'ChatBot',
    CustomerSupport = 'Customer Support',
    NapoleonCustomerSupport = 'Inbound Care Team',
    Vip = 'VIP',
}
let flags: ZendeskMetaDataFlags = {};

const getWidgetType = () => {
    if (window.zE?.widget === 'classic') {
        return 'webWidget';
    }
    return 'messenger';
};

const isZenDeskLoaded = (): boolean => {
    return (
        (typeof window.zE?.version !== 'undefined' || typeof window.zE?.widget !== 'undefined')
        && typeof window.zE === 'function'
    );
};

let helpShownCallback : undefined | (() => void);
let helpHiddenCallback : undefined | (() => void);

export function setupHelpShownCallback(callback : () => void) : void {
    helpShownCallback = callback;
}

export function setupHelpHiddenCallback(callback : () => void) : void {
    helpHiddenCallback = callback;
}

export async function showHelp(): Promise<void> {
    if (!isZenDeskLoaded()) {
        await initializeHelpBot();
    }
    checkLoading();
    window.zE(getWidgetType(), 'show');
}

export async function openHelp(shouldHideOnClose = true, loadingTimeoutText = '' as any): Promise<void> {
    checkLoading(loadingTimeoutText);
    if (!isZenDeskLoaded() && !(await initializeHelpBot())) {
        return;
    }
    if (getWidgetType() === 'webWidget') {
        window.zE?.activate?.({ hideOnClose: shouldHideOnClose });
    } else {
        if (getAccountConfig().useZendeskTimeout) {
            setTimeout(() => {
                openMessenger();
            }, parseInt(getAccountConfig().zendeskTimeoutValue));
        }
    }
}

export async function setHelperUserData(): Promise<void> {
    if (!isZenDeskLoaded()) {
        return;
    }
    const user = store.getters['data/authentication/user'];
    if (user) {
        if (getWidgetType() === 'webWidget') {
            setHelpUser(user);
            const [department] = window.zE('webWidget:get', 'chat:departments');
            pickDepartment(department);
        } else {
            if (getAccountConfig().isZendeskAuthenticationEnabled) {
                const token = await ZendeskService.getInstance().getAuthenticationToken({
                    email: user.email,
                    name: `${user.firstName} ${user.lastName}`,
                    id: user.userUuid,
                });
                loginMessengerUser(user, token);
            }
        }
    } else {
        deleteLegacyHelpUser();
    }
}

// Added
export function clearHelperUserData() {
    if (getWidgetType() === 'webWidget') {
        deleteLegacyHelpUser();
    } else {
        window.zE?.('messenger', 'logoutUser');
    }
}

function deleteLegacyHelpUser(): void {
    if (getWidgetType() === 'webWidget') {
        window.zE?.('webWidget', 'clear');
    }
}

function loginMessengerUser(user: ZendeskUserData, token: string) {
    window.zE?.('messenger', 'loginUser', (cb: (a: unknown) => void) => {
        cb(token);
    });
    if (flags.IS_VIP) {
        window.zE('messenger:set', 'conversationFields', [
            {
                id: flags.IS_VIP,
                value: (user.isVip) ? 'vip' : 'novip',
            },
        ]);
    }
}
function setHelpUser(user: ZendeskUserData): void {
    window.zE('webWidget', 'prefill', {
        name: {
            value: `${user.firstName} ${user.lastName}`,
        },
        email: {
            value: user.email,
        },
        phone: {
            value: user.phone,
        },
    });
}

/**
 *
 * because zenDesk changes context help only on page refresh, this is SPA method fot same functionality
 */
export function setZenDeskContextHelpKey(path?: string): void {
    if (!isZenDeskLoaded()) {
        return;
    }
    const pathKeys = {
        [Link.App.Home]: Link.Sport.PrematchOffer,
        [Link.GrowthMarketing.LandingIntro]: Link.App.TermsAndConditions,
    };
    const pathName = path || window.location.pathname;
    const search = pathKeys[pathName] || pathName;
    if (getWidgetType() === 'webWidget') {
        window.zE('webWidget', 'helpCenter:setSuggestions', { search });
    } else {
        window.zE('messenger:set', 'conversationTags', [search]);
    }
}

function checkLoading(loadingTimeoutText?: any) {
    setTimeout(() => {
        const iframes = document.getElementsByTagName('iframe');
        const zendesk = Array.from(iframes).find((iframe) => iframe.getAttribute('title') === iframeTitle[getConfig().appLocale.locale]);

        if (zendesk) {
            const spinner = zendesk.contentDocument!.querySelectorAll('[class*="StyledSVG"]');
            if (spinner.length) {
                AnalyticsSDK.getInstance().log('CS_Email_Window');

                spinner.forEach((element) => {
                    const existingLoadingTimeoutElement = zendesk.contentDocument!.querySelector('.loading-timeout-text');
                    if (!existingLoadingTimeoutElement) {
                        const newDiv = zendesk.contentDocument!.createElement('div');
                        newDiv.style.fontSize = '14px';
                        newDiv.style.color = 'rgb(47, 57, 65)';
                        newDiv.style.fontWeight = 'bold';
                        newDiv.style.width = '80%';
                        newDiv.style.wordBreak = 'break-word';
                        newDiv.innerHTML = loadingTimeoutText || '';
                        const parentElement = element.parentNode;
                        if (parentElement) {
                            parentElement.appendChild(newDiv);
                        }
                    }
                    // remove spinner element
                    element.remove();
                });
            }
        }
    }, getAccountConfig().zendeskLoadingTimeCheck);
}

// eslint-disable-next-line sonarjs/cognitive-complexity
export async function initializeHelpBot(options?: { openHidden?: boolean }): Promise<boolean> {
    if (!options?.openHidden) {
        showLoading();
    }
    const isZendeskLoaded = await loadZendeskChat();

    if (!options?.openHidden) {
        hideLoading();
    }
    if (getWidgetType() === 'webWidget') {
        if (options?.openHidden) {
            window.zE.hide();
        }
        window.zE('webWidget', 'updateSettings', {
            webWidget: {
                chat: {
                    departments: {
                        enabled: [],
                    },
                },
            },
        });
        window.zE('webWidget', 'setLocale', languageMap[getConfig().appLocale.locale]);
        window.zE('webWidget:on', 'chat:connected', () => {
            const departments: DepartmentEntry[] = window.zE('webWidget:get', 'chat:departments');
            departments.forEach((department) => {
                pickDepartment(department);
            });
        });
        window.zE('webWidget:on', 'chat:departmentStatus', (department: DepartmentEntry) => {
            pickDepartment(department);
        });
        // Open the widget on unread messages
        window.zE('webWidget:on', 'chat:unreadMessages', () => {
            window.zE.show();
            window.zE('webWidget', 'open');
            helpShownCallback?.();
        });
        window.zE('webWidget:on', 'close', () => {
            window.zE('webWidget', 'close');
            helpHiddenCallback?.();
        });
        setHelperUserData();
        setZenDeskContextHelpKey();
    } else {
        window.zE('messenger:set', 'locale', languageMap[getConfig().appLocale.locale]);
        setHelperUserData();

        if (getAccountConfig().useZendeskTimeout) {
            setTimeout(() => {
                window.zE('messenger:on', 'close', () => {
                    window.zE?.('messenger', 'hide');
                    helpHiddenCallback?.();
                });
                window.zE('messenger:on', 'unreadMessages', () => {
                    const isEventAlreadySent = true;
                    openMessenger(isEventAlreadySent);
                });
                if (options?.openHidden) {
                    window.zE('messenger', 'hide');
                    window.zE('messenger', 'close');
                } else {
                    window.zE('messenger', 'open');
                    helpShownCallback?.();
                }
            }, parseInt(getAccountConfig().zendeskTimeoutValue));
        } else {
            window.zE('messenger:on', 'close', () => {
                window.zE?.('messenger', 'hide');
                helpHiddenCallback?.();
            });
            window.zE('messenger:on', 'unreadMessages', () => {
                const isEventAlreadySent = true;
                openMessenger(isEventAlreadySent);
            });

            if (options?.openHidden) {
                window.zE('messenger', 'hide');
                window.zE('messenger', 'close');
            } else {
                window.zE('messenger', 'open');
                helpShownCallback?.();
            }
        }
    }
    return isZendeskLoaded;
}

/**
 * Loads scripts needed for initialization of ZenDesk.
 * ZenDesk doesn't have method that listens when ZenDesk is completely loaded, so this interval check is required
 */
async function loadZendeskChat(): Promise<boolean> {
    if (isZenDeskLoaded()) {
        return true;
    }
    const zendeskKey = getConfig().country.zendeskKey;
    const zdAssetsUrl = `https://static.zdassets.com/ekr/snippet.js?key=${zendeskKey}`;
    await loadScript(zdAssetsUrl, 'ze-snippet');

    return new Promise((resolve) => {
        const zdOnLoad = setInterval(() => {
            if (isZenDeskLoaded()) {
                clearInterval(zdOnLoad);
                resolve(true);
            }
        }, 50);
        setTimeout(() => {
            clearInterval(zdOnLoad);
            resolve(false);
        }, 5000);
    });
}

function showLoading(): void {
    document.getElementById('zendesk-loading')?.classList.add('show');
}

function hideLoading(): void {
    document.getElementById('zendesk-loading')?.classList.replace('show', 'hide');
}

function pickDepartment(department: DepartmentEntry): void {
    const user = store.getters['data/authentication/user'];
    const departments: DepartmentEntry[] = window.zE('webWidget:get', 'chat:departments');

    const departmentMap = departments?.reduce?.((map, department) => {
        return { ...map, [department.name]: department };
    }, {} as { [key in Department]: DepartmentEntry });
    if (user?.isVip && departmentMap[Department.Vip]?.status === 'online') {
        return updateZenDeskActiveDepartment(Department.Vip);
    }
    if (department.name === Department.ChatBot && department.status === 'online') {
        updateZenDeskActiveDepartment(Department.ChatBot);
    } else if (department.name === Department.ChatBot && department.status !== 'online') {
        if (departmentMap[Department.CustomerSupport]?.status === 'online') {
            updateZenDeskActiveDepartment(Department.CustomerSupport);
        } else if (departmentMap[Department.NapoleonCustomerSupport]?.status === 'online') {
            updateZenDeskActiveDepartment(Department.NapoleonCustomerSupport);
        }
    }
}

function updateZenDeskActiveDepartment(department: Department): void {
    if (getWidgetType() === 'webWidget') {
        window.zE('webWidget', 'updateSettings', {
            webWidget: {
                chat: {
                    departments: {
                        enabled: [''],
                        select: department,
                    },
                },
            },
        });
    }
}

function openMessenger(isEventAlreadySent = false) {
    window.zE('messenger', 'show');
    window.zE('messenger', 'open');

    if (!isEventAlreadySent) {
        AnalyticsSDK.getInstance().log('CS_Chat_Open');
    }
}

export function setZendeskFlags(newFlags: ZendeskMetaDataFlags) {
    flags = newFlags;
}
